if (!customElements.get('wls-main-search')) {
  class WLSMainSearch extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onPopState = this._onPopState.bind(this);
      this._boostCache = new Map();
      this._boostJsonCache = new Map();
      this._shopifyCache = new Map();
      this._abortController = null;
      this._booted = false;
      this._boostRetryTimer = null;
      this._titleReady = false;
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick, true);
      window.addEventListener('popstate', this._onPopState);
      setTimeout(() => this._boot(), 0);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick, true);
      window.removeEventListener('popstate', this._onPopState);
      this._abortController?.abort();
      if (this._boostRetryTimer) clearTimeout(this._boostRetryTimer);
    }

    _currentType() {
      return this.querySelector('wls-items-facet')?.dataset.wlsSearchResultType
        || new URL(window.location.href).searchParams.get('type')
        || 'product';
    }

    _boot() {
      if (this._booted) return;
      this._booted = true;
      this._restoreProductCount();
      this._loadTabCounts();
      if (this._currentType() === 'product') this._loadBoostProducts();
      else this._loadBoostProductCount();
    }

    _query() {
      return new URL(window.location.href).searchParams.get('q') || this.dataset.boostQ || '';
    }

    _limit() {
      return Number(this.dataset.boostLimit || 8);
    }

    _searchRoot() {
      return `${window.Shopify?.routes?.root || '/'}search`.replace(/\/{2,}/g, '/');
    }

    _productCountKey(query) {
      return `wls-boost-product-count:${query}`;
    }

    _setCount(type, count) {
      const btn = this.querySelector(`.js-search-nav-btn[data-type="${type}"]`);
      if (!btn) return;
      btn.setAttribute('data-wls-results-count', String(count));
      const span = btn.querySelector('.js-search-tab-count');
      if (span) span.textContent = String(count);
      btn.querySelector('.js-search-tab-count-wrap')?.removeAttribute('hidden');
      if (type === 'product') {
        try {
          sessionStorage.setItem(this._productCountKey(this._query()), String(count));
        } catch (err) { /* ignore quota / private mode */ }
      }
      this._updateResultsHeadings();
    }

    _setResultsHeading(text) {
      document.title = text;
      const hero = document.querySelector('.js-search-hero-title');
      if (hero) hero.textContent = text;
    }

    _updateResultsHeadings() {
      const query = this._query();
      const quoted = query ? `"${query}"` : '';
      const pending = query ? `Search results for ${quoted}` : 'Search';
      const types = ['product', 'article'];
      const counts = types.map((type) => {
        const raw = this.querySelector(`.js-search-nav-btn[data-type="${type}"]`)?.getAttribute('data-wls-results-count');
        if (raw == null || raw === '') return null;
        const value = Number(raw);
        return Number.isNaN(value) ? null : value;
      });

      if (counts.some((count) => count == null)) {
        if (!this._titleReady) this._setResultsHeading(pending);
        return;
      }

      const total = counts.reduce((sum, count) => sum + count, 0);
      this._titleReady = true;
      const noun = total === 1 ? 'Search result' : 'Search results';
      this._setResultsHeading(query ? `${total} ${noun} for ${quoted}` : `${total} ${noun}`);
    }

    _restoreProductCount() {
      try {
        const cached = sessionStorage.getItem(this._productCountKey(this._query()));
        if (cached != null && cached !== '') this._setCount('product', cached);
      } catch (err) { /* ignore */ }
    }

    _scrollToSearch() {
      const section = this.closest('.wls-main-search') || this;
      const top = section.getBoundingClientRect().top + window.scrollY - 50;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    _setActiveTab(type) {
      this.querySelectorAll('.js-search-nav-btn').forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.type === type);
      });
    }

    _onClick(event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const link = event.target.closest('a[href]');
      if (!link || !this.contains(link)) return;

      const isTab = link.classList.contains('js-search-nav-btn');
      const isPage = Boolean(link.closest('wls-pagination, .js-wls-product-pagination, .wls-pagination'));

      if (isTab && link.classList.contains('active')) return;
      if (!isTab && !isPage) return;

      event.preventDefault();
      event.stopPropagation();
      this._navigate(link.href, { scroll: isPage });
    }

    _onPopState() {
      this._navigate(window.location.href, { push: false });
    }

    async _navigate(href, { push = true, scroll = false } = {}) {
      const url = new URL(href, window.location.origin);
      const type = url.searchParams.get('type') || 'product';
      if (push) history.pushState({}, '', url);
      this._setActiveTab(type);

      this._abortController?.abort();
      this._abortController = new AbortController();
      const { signal } = this._abortController;

      try {
        if (type === 'product') {
          await this._ensureProductFacet(url, signal);
          await this._loadBoostProducts(url, signal);
        } else {
          await this._swapShopifyResults(url, signal);
        }
        if (scroll) this._scrollToSearch();
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }

    async _fetchSectionDoc(url) {
      const sectionId = this.dataset.wlsSearchSectionId;
      const key = `${url.searchParams.get('type') || 'product'}:${url.searchParams.get('page') || 1}:${url.searchParams.get('q') || ''}`;
      if (this._shopifyCache.has(key)) return this._shopifyCache.get(key);

      const request = new URL(url.toString());
      if (sectionId) request.searchParams.set('sections', sectionId);

      const promise = fetch(request, { headers: { Accept: 'application/json' } })
        .then((res) => res.json())
        .then((data) => {
          const html = data[sectionId];
          if (!html) throw new Error('Missing section');
          return new DOMParser().parseFromString(html, 'text/html');
        })
        .catch((err) => {
          this._shopifyCache.delete(key);
          throw err;
        });

      this._shopifyCache.set(key, promise);
      return promise;
    }

    _setResultsLoading(isLoading) {
      const facet = this.querySelector('wls-items-facet');
      const loader = this.querySelector('.js-wls-search-loader');
      facet?.classList.toggle('wls-loading-state', Boolean(isLoading));
      if (!loader) return;
      if (isLoading) {
        loader.removeAttribute('hidden');
      } else {
        loader.setAttribute('hidden', '');
      }
    }

    async _ensureProductFacet(url, signal) {
      if (this.querySelector('.js-wls-product-grid')) return;
      this._setResultsLoading(true);
      const doc = await this._fetchSectionDoc(url);
      if (signal?.aborted) return;
      this._applyFacet(doc, 'product');
      this._setResultsLoading(true);
    }

    async _swapShopifyResults(url, signal) {
      const type = url.searchParams.get('type');
      const facet = this.querySelector('wls-items-facet');
      facet?.classList.add('wls-loading-state');
      try {
        const doc = await this._fetchSectionDoc(url);
        if (signal?.aborted) return;
        this._applyFacet(doc, type);
        const count = this._shopifyCountFromDoc(doc, type);
        if (count != null) this._setCount(type, count);
      } finally {
        this._setResultsLoading(false);
        facet?.classList.remove('wls-loading-state');
      }
    }

    _applyFacet(doc, type) {
      const current = this.querySelector('wls-items-facet');
      const next = doc.querySelector('wls-items-facet');
      if (!current || !next) return;
      current.innerHTML = next.innerHTML;
      current.setAttribute('data-wls-search-result-type', next.getAttribute('data-wls-search-result-type') || type);
      const resultCount = next.getAttribute('data-wls-result-count');
      if (resultCount != null && resultCount !== '') {
        current.setAttribute('data-wls-result-count', resultCount);
      } else {
        current.removeAttribute('data-wls-result-count');
      }
    }

    async _loadBoostProductCount() {
      const query = this._query();
      if (!query) return;

      const params = new URLSearchParams({
        shop: this.dataset.boostShop || window.Shopify?.shop || '',
        q: query,
        limit: '1',
        page: '1',
        event_type: 'init',
      });

      try {
        const apiUrl = this.dataset.boostApi || 'https://services.mybcapps.com/bc-sf-filter/search';
        const data = await this._request(`${apiUrl}?${params}`);
        this._setCount('product', Number(data.total_product || 0));
      } catch (err) { /* keep cached / Liquid count */ }
    }

    _shopifyCountFromDoc(doc, type) {
      const facet = doc.querySelector('wls-items-facet');
      const facetType = facet?.getAttribute('data-wls-search-result-type');
      const facetCount = facet?.getAttribute('data-wls-result-count');
      if (facetType === type && facetCount != null && facetCount !== '') return facetCount;

      const tabCount = doc.querySelector(`.js-search-nav-btn[data-type="${type}"]`)?.getAttribute('data-wls-results-count');
      if (tabCount != null && tabCount !== '') return tabCount;

      const marker = doc.querySelector('.js-wls-tab-count');
      if (marker?.hasAttribute('data-count')) return marker.getAttribute('data-count');

      return null;
    }

    async _loadTabCounts() {
      const query = this._query();
      if (!query) return;

      ['article'].forEach((type) => {
        const url = new URL(this._searchRoot(), window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('type', type);
        url.searchParams.set('view', 'tab-count');
        fetch(url)
          .then((res) => {
            if (!res.ok) throw new Error(String(res.status));
            return res.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const count = this._shopifyCountFromDoc(doc, type);
            if (count != null) this._setCount(type, count);
          })
          .catch(() => {});
      });
    }

    _wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async _request(url, { retries = 3, responseType = 'json', timeout = 8000 } = {}) {
      let lastError;
      for (let attempt = 0; attempt <= retries; attempt += 1) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeout);
        try {
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return responseType === 'text' ? res.text() : res.json();
        } catch (err) {
          lastError = err;
          if (attempt < retries) await this._wait(300 * (attempt + 1));
        } finally {
          clearTimeout(timer);
        }
      }
      throw lastError;
    }

    _isInconsistentBoostPage(data, page, limit) {
      const total = Number(data?.total || 0);
      const count = data?.products?.length || 0;
      if (count > 0 || total <= 0) return false;
      const lastPage = Math.max(1, Math.ceil(total / limit));
      return page <= lastPage;
    }

    _showEmptyProducts(grid, pagination, empty) {
      if (grid) grid.innerHTML = '';
      pagination?.replaceChildren();
      empty?.removeAttribute('hidden');
    }

    _boostCacheKey(query, page, limit) {
      return `${query}::${page}::${limit}`;
    }

    _visiblePages(currentPage, totalPages) {
      const pages = [];
      for (let i = 1; i <= totalPages; i += 1) {
        if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) {
          pages.push(i);
        }
      }
      return pages;
    }

    _isBoostReady(query, page, limit) {
      return Boolean(this._boostCache.get(this._boostCacheKey(query, page, limit))?._ready);
    }

    _cachePromise(map, key, factory) {
      if (map.has(key)) return map.get(key);

      const promise = factory().catch((err) => {
        map.delete(key);
        throw err;
      });
      map.set(key, promise);
      return promise;
    }

    _getBoostJson(query, page, limit) {
      return this._cachePromise(this._boostJsonCache, this._boostCacheKey(query, page, limit), () => (
        this._fetchBoostJson(query, page, limit)
      ));
    }

    _getBoostPage(query, page, limit) {
      const key = this._boostCacheKey(query, page, limit);
      if (this._boostCache.has(key)) return this._boostCache.get(key);

      const promise = this._fetchBoostPage(query, page, limit)
        .then((result) => {
          promise._ready = true;
          return result;
        })
        .catch((err) => {
          this._boostCache.delete(key);
          throw err;
        });
      promise._ready = false;
      this._boostCache.set(key, promise);
      return promise;
    }

    async _fetchBoostJson(query, page, limit) {
      const params = new URLSearchParams({
        shop: this.dataset.boostShop || window.Shopify?.shop || '',
        q: query,
        limit: String(limit),
        page: String(page),
        event_type: page > 1 ? 'page' : 'init',
      });

      const apiUrl = this.dataset.boostApi || 'https://services.mybcapps.com/bc-sf-filter/search';
      const load = async () => {
        const data = await this._request(`${apiUrl}?${params}`);
        const products = Array.isArray(data?.products) ? data.products : [];
        return {
          products,
          total: Number(data?.total_product || 0),
          handles: products.map((product) => product.handle).filter(Boolean),
        };
      };

      let result = await load();
      if (this._isInconsistentBoostPage(result, page, limit)) {
        await this._wait(300);
        result = await load();
      }
      if (this._isInconsistentBoostPage(result, page, limit)) {
        throw new Error('Inconsistent Boost page');
      }
      return result;
    }

    async _fetchProductCards(handles) {
      const cardsUrl = new URL(this._searchRoot(), window.location.origin);
      cardsUrl.searchParams.set('view', 'boost-cards');
      cardsUrl.searchParams.set('q', handles.join(' '));
      const cardsHtml = await this._request(cardsUrl, { responseType: 'text' });
      const cardsDoc = new DOMParser().parseFromString(cardsHtml, 'text/html');
      return cardsDoc.querySelector('.wls-boost-search-cards')?.innerHTML || '';
    }

    async _fetchBoostPage(query, page, limit) {
      const { products, total, handles } = await this._getBoostJson(query, page, limit);
      if (!handles.length) return { products, total, cards: '' };

      let cards = await this._fetchProductCards(handles);
      if (!cards) {
        await this._wait(300);
        cards = await this._fetchProductCards(handles);
      }
      if (!cards) throw new Error('Missing product cards');

      return { products, total, cards };
    }

    _prefetchVisiblePages(query, page, limit, totalPages) {
      this._visiblePages(page, totalPages).forEach((nextPage) => {
        if (nextPage !== page) this._getBoostPage(query, nextPage, limit).catch(() => {});
      });
    }

    _applyBoostResult(result, grid, empty, page, limit, query) {
      this._setCount('product', result.total);
      const totalPages = Math.max(1, Math.ceil(result.total / limit));
      this._prefetchVisiblePages(query, page, limit, totalPages);
      empty?.setAttribute('hidden', '');
      grid.innerHTML = result.cards;
      this._renderPagination(page, totalPages, query);
    }

    _retryBoostInBackground(url, signal, query, page, limit, attempt = 0) {
      const maxAttempts = 8;
      if (this._boostRetryTimer) clearTimeout(this._boostRetryTimer);
      if (attempt >= maxAttempts) {
        this._setResultsLoading(false);
        return;
      }

      this._setResultsLoading(true);
      this._boostRetryTimer = setTimeout(async () => {
        this._boostRetryTimer = null;
        if (signal?.aborted) return;
        this._boostCache.delete(this._boostCacheKey(query, page, limit));
        this._boostJsonCache.delete(this._boostCacheKey(query, page, limit));
        try {
          const result = await this._getBoostPage(query, page, limit);
          if (signal?.aborted) return;
          const grid = this.querySelector('.js-wls-product-grid');
          const empty = this.querySelector('.js-wls-product-empty');
          const pagination = this.querySelector('.js-wls-product-pagination');
          if (!grid) return;

          if (!result.products.length) {
            this._setCount('product', 0);
            this._showEmptyProducts(grid, pagination, empty);
            this._setResultsLoading(false);
            return;
          }

          if (!result.cards) {
            this._retryBoostInBackground(url, signal, query, page, limit, attempt + 1);
            return;
          }

          this._applyBoostResult(result, grid, empty, page, limit, query);
          this._setResultsLoading(false);
        } catch (err) {
          if (err?.name === 'AbortError') return;
          this._retryBoostInBackground(url, signal, query, page, limit, attempt + 1);
        }
      }, 700 * (attempt + 1));
    }

    async _loadBoostProducts(fromUrl, signal) {
      const url = fromUrl || new URL(window.location.href);
      const query = url.searchParams.get('q') || this._query();
      const grid = this.querySelector('.js-wls-product-grid');
      const empty = this.querySelector('.js-wls-product-empty');
      const pagination = this.querySelector('.js-wls-product-pagination');
      if (!grid) {
        this._setResultsLoading(false);
        return;
      }
      if (!query) {
        this._setResultsLoading(false);
        return;
      }

      const page = Number(url.searchParams.get('page') || 1) || 1;
      const limit = this._limit();
      const ready = this._isBoostReady(query, page, limit);
      if (!ready) this._setResultsLoading(true);

      let keepLoading = false;
      try {
        const pagePromise = this._getBoostPage(query, page, limit);
        this._getBoostJson(query, page, limit)
          .then((data) => {
            if (signal?.aborted) return;
            const totalPages = Math.max(1, Math.ceil(data.total / limit));
            this._prefetchVisiblePages(query, page, limit, totalPages);
          })
          .catch(() => {});

        const result = await pagePromise;
        if (signal?.aborted) return;

        if (!result.products.length) {
          this._setCount('product', 0);
          this._showEmptyProducts(grid, pagination, empty);
          return;
        }

        if (!result.cards) {
          keepLoading = true;
          this._retryBoostInBackground(url, signal, query, page, limit);
          return;
        }

        this._applyBoostResult(result, grid, empty, page, limit, query);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        keepLoading = true;
        this._retryBoostInBackground(url, signal, query, page, limit);
      } finally {
        if (!keepLoading) this._setResultsLoading(false);
      }
    }

    _pageHref(query, page) {
      const url = new URL(window.location.href);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('page', String(page));
      return url.pathname + url.search;
    }

    _clonePaginationTemplate(name) {
      const tpl = this.querySelector(`template[data-wls-pagination-template="${name}"]`);
      return tpl?.content.firstElementChild?.cloneNode(true) || null;
    }

    _fillPaginationNav(nav, currentPage, totalPages, query) {
      const prev = currentPage > 1
        ? this._clonePaginationTemplate('prev-link')
        : this._clonePaginationTemplate('prev-disabled');
      const list = this._clonePaginationTemplate('pages');
      const next = currentPage < totalPages
        ? this._clonePaginationTemplate('next-link')
        : this._clonePaginationTemplate('next-disabled');
      if (!prev || !list || !next) return;

      if (currentPage > 1) {
        prev.href = this._pageHref(query, currentPage - 1);
        prev.dataset.page = String(currentPage - 1);
      }
      if (currentPage < totalPages) {
        next.href = this._pageHref(query, currentPage + 1);
        next.dataset.page = String(currentPage + 1);
      }

      const visible = this._visiblePages(currentPage, totalPages);
      visible.forEach((i, index) => {
        if (index > 0 && i - visible[index - 1] > 1) {
          const ellipsis = this._clonePaginationTemplate('ellipsis');
          if (ellipsis) list.appendChild(ellipsis);
        }

        if (i === currentPage) {
          const item = this._clonePaginationTemplate('page-current');
          const label = item?.querySelector('.wls-pagination__page');
          if (label) label.textContent = String(i);
          if (item) list.appendChild(item);
          return;
        }

        const item = this._clonePaginationTemplate('page-link');
        const link = item?.querySelector('a');
        if (link) {
          link.href = this._pageHref(query, i);
          link.dataset.page = String(i);
          link.textContent = String(i);
        }
        if (item) list.appendChild(item);
      });

      nav.replaceChildren(prev, list, next);
    }

    _renderPagination(currentPage, totalPages, query) {
      const wrap = this.querySelector('.js-wls-product-pagination');
      if (!wrap) return;
      wrap.replaceChildren();
      if (totalPages <= 1) return;

      const root = this._clonePaginationTemplate('root');
      if (!root) return;

      root.querySelectorAll('.wls-pagination__nav').forEach((nav) => {
        this._fillPaginationNav(nav, currentPage, totalPages, query);
      });
      wrap.appendChild(root);
    }
  }

  customElements.define('wls-main-search', WLSMainSearch);
}
